import React, { memo, useMemo, useState } from 'react';
import moment from 'moment';
import { toastr } from 'react-redux-toastr';
import useIntl from 'hooks/useIntl';
import {
  resolveActualDateLabel,
  resolveBudgetSections,
  differencesToHighlight,
  getQueryStringValue,
} from '../utils';
import { TableBudget } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { CalculateIcon } from '@fortress-technology-solutions/fortress-component-library/Icons';
import {
  Stack,
  Typography,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { formatCurrency } from '@fortress-technology-solutions/fortress-component-library/utils';
import AutoCalculateModal from 'containers/RentForecast/RDBudgetModal/autoCalculateModal';
import { fieldCalculations } from 'containers/RentForecast/RDBudgetModal/calculations';
import messages from './messages';
import rentForecastMessages from '../messages';
import { FormattedMessage } from 'react-intl';
import type {
  Part1ColumnKey,
  SectionConfiguration,
  BudgetType,
} from 'containers/RentForecast/RDBudgetModal/types';

export const data: SectionConfiguration<Part1ColumnKey>[] = [
  {
    header: 'Operational Cash Sources',
    rows: [
      {
        label: '1. Rental Income',
        labelTooltip:
          'This amount equals the Total Basic Rent Income from the rent forecast.',
        disabledWhen: {
          proposedBudget: ['normal'],
        },
      },
      {
        label: '2. RHS Rental Assistance Received',
      },
      {
        label: '3. Application Fees Received',
      },
      {
        label: '4. Laundry and Vending',
      },
      {
        label: '5. Interest Income',
      },
      {
        label: '6. Tenant Charges',
      },
      {
        label: '7. Other - Project Sources',
      },
      {
        label: '8. Less',
        labelSuffix: '(Vacancy and Contingency Allowance)',
        disabledWhen: {
          proposedBudget: ['normal'],
          comments: ['normal'],
        },
        dynamicTooltip: ({ parameters }) => (
          <Stack direction="column" spacing={1}>
            <Typography variant="titleSmall">
              <FormattedMessage
                {...rentForecastMessages.rdBudgetFormTooltipProposedValue}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage
                {...rentForecastMessages.rdBudgetFormPartOneLessVacancyTooltipLine1}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage
                {...rentForecastMessages.rdBudgetFormPartOneLessVacancyTooltipLine2}
                values={parameters}
              />
            </Typography>
            <Typography variant="bodySmall">
              <FormattedMessage
                {...rentForecastMessages.rdBudgetFormPartOneLessVacancyTooltipLine3}
              />
            </Typography>
          </Stack>
        ),
      },
      {
        label: '9. Less',
        labelSuffix: '(Agency Approved Incentive Allowance)',
      },
      {
        label: '10. Sub-Total',
        labelSuffix: '[(1 thru 7) - (8 & 9)]',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Non-Operational Cash Sources',
    rows: [
      {
        label: '11. Cash - Non Project',
      },
      {
        label: '12. Authorized Loan',
        labelSuffix: '(Non-RHS)',
      },
      {
        label: '13. Transfer From Reserve',
        labelTooltip:
          'This amount is the total of the Annual Capital Budget, Building and Equipment Repair, ' +
          'and Other Non-Operating Expenses from Part III, Lines 4-6.',
        disabledWhen: {
          proposedBudget: ['normal'],
        },
      },
      {
        label: '14. Sub-Total',
        labelSuffix: '(11 thru 13)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '15. Total Cash Sources',
        labelSuffix: '(10+14)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Operational Cash Uses',
    rows: [
      {
        label: '16. Total O&M Expenses',
        labelSuffix: '(From Part II)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '17. RHS Debt Payment',
        labelTooltip: (
          <FormattedMessage
            {...rentForecastMessages.rdBudgetFormTooltipProposedAsCurrent}
          />
        ),
      },
      {
        label: '18. RHS Payment',
        labelSuffix: '(Overage)',
      },
      {
        label: '19. RHS Payment',
        labelSuffix: '(Late Fee)',
      },
      {
        label: '20. Reduction in Prior Year Payables',
      },
      {
        label: '21. Tenant Utility Payments',
      },
      {
        label: '22. Transfer to Reserve',
        labelTooltip: (
          <FormattedMessage
            {...rentForecastMessages.rdBudgetFormTooltipProposedAsCurrent}
          />
        ),
      },
      {
        label: '23. Return to Owner / NP Asset Management Fee',
        labelTooltip: (
          <FormattedMessage
            {...rentForecastMessages.rdBudgetFormTooltipProposedAsCurrent}
          />
        ),
      },
      {
        label: '24. Sub-Total',
        labelSuffix: '(16 thru 23)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Non-Operational Cash Uses',
    rows: [
      {
        label: '25. Authorized Debt Payment',
        labelSuffix: '(Non-RHS)',
        labelTooltip: (
          <FormattedMessage
            {...rentForecastMessages.rdBudgetFormTooltipProposedAsCurrent}
          />
        ),
      },
      {
        label: '26. Annual Capital Budget',
        labelSuffix: '(From Part III, Lines 4-6)',
        disabledWhen: {
          proposedBudget: ['normal'],
        },
      },
      {
        label: '27. Miscellaneous',
        labelTooltip: (
          <FormattedMessage
            {...rentForecastMessages.rdBudgetFormTooltipProposedAsCurrent}
          />
        ),
      },
      {
        label: '28. Sub-Total',
        labelSuffix: '(25 thru 27)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '29. Total Cash Uses',
        labelSuffix: '(24+28)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
      {
        label: '30. Net Cash Deficit',
        labelSuffix: '(15-29)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
  {
    header: 'Cash Balance',
    rows: [
      {
        label: '31. Beginning Cash Balance',
        labelTooltip:
          'Actual = General Operating Beginning Balance' +
          ' + Real Estate Tax & Insurance Escrow Beginning Balance',
        disabledWhen: {
          actual: ['normal'],
        },
      },
      {
        label: '32. Accrual to Cash Adjustment',
      },
      {
        label: '33. Ending Cash Balance',
        labelSuffix: '(30+31+32)',
        disabledWhen: {
          currentBudget: ['custom'],
          actual: ['normal', 'custom'],
          proposedBudget: ['normal', 'custom'],
        },
      },
    ],
  },
];

export const generateHeaders = (
  year,
  actualDateRange,
  differenceCount,
  enableAutoCalculate,
  budgetType: BudgetType,
  setOpenAutoCalculate,
  rentForecastCreatedDate: string,
  disableIntacctActuals: boolean = false,
) => {
  return [
    {
      label: 'Modified After Intacct sync',
      subLabel: `${differenceCount} total items across Parts I, II, III, and V`,
      legend: { backgroundColor: 'warning.lighter' },
      flexSize: 1.6,
      textAlign: 'left',
      sticky: true,
      hidden: budgetType === 'custom',
      hideHeader: budgetType === 'custom',
    },
    {
      label: `${budgetType === 'approved' ? 'Approved' : 'Current'} Budget`,
      subLabel: `01/01/${year} - 12/31/${year}`,
      key: 'currentBudget',
      flexSize: 1,
      emptyValue: 0,
      type: 'number-currency',
      disabled:
        budgetType === 'disabled' ||
        budgetType === 'custom-disabled' ||
        budgetType === 'normal' ||
        budgetType === 'approved',
    },
    {
      label: 'Actual',
      subLabel: resolveActualDateLabel(year, actualDateRange),
      key: 'actual',
      flexSize: 1,
      emptyValue: 0,
      type: 'number-currency',
      disabled:
        budgetType === 'disabled' ||
        budgetType === 'custom-disabled' ||
        budgetType === 'approved',
    },
    {
      label: 'Proposed Budget',
      subLabel: `01/01/${parseInt(year) + 1} - 12/31/${parseInt(year) + 1}`,
      key: 'proposedBudget',
      flexSize: 1,
      type: 'number-currency',
      emptyValue: 0,
      ...(budgetType === 'normal'
        ? {
            action: {
              label: 'Auto-Calculate',
              startIcon: <CalculateIcon />,
              onClick: () => setOpenAutoCalculate(true),
            },
          }
        : {}),
      disabled:
        budgetType === 'disabled' ||
        budgetType === 'custom-disabled' ||
        budgetType === 'approved',
    },
    ...(budgetType?.startsWith('custom')
      ? [
          {
            label: 'YTD Actuals',
            subLabel: resolveActualDateLabel(year, {
              from: moment(rentForecastCreatedDate).startOf('year'),
              to: moment(rentForecastCreatedDate),
            }),
            key: 'intacctActual',
            flexSize: 1,
            emptyValue: 0,
            type: 'number-currency',
            disabled: budgetType === 'custom-disabled' || disableIntacctActuals,
          },
        ]
      : []),
    {
      label: 'Comments',
      key: 'comments',
      textAlign: 'left',
      flexSize: 1,
      placeholder: 'Comment',
      type: 'text',
      disabled:
        budgetType === 'disabled' ||
        budgetType === 'custom-disabled' ||
        budgetType === 'approved',
    },
  ];
};

const Part1 = memo(
  ({
    useFormReturn,
    intacctDifferences,
    actualDateRange,
    rentForecast,
    enableAutoCalculate,
    budgetType,
  }) => {
    const intl = useIntl();
    const [highlightFields, differenceCount] = useMemo(
      () => differencesToHighlight(intacctDifferences),
      [intacctDifferences],
    );
    const [openAutoCalculate, setOpenAutoCalculate] = useState(false);
    const [proposedRentalIncomeRaw, vacancyRateRaw] = useFormReturn.watch([
      '0-0-0-proposedBudget',
      '0-0-7-comments',
    ]);

    const sections = useMemo(
      () =>
        resolveBudgetSections(data, budgetType, {
          parameters: {
            proposedRentalIncome: formatCurrency(proposedRentalIncomeRaw),
            vacancyRate: formatCurrency(vacancyRateRaw),
          },
        }),
      [rentForecast, proposedRentalIncomeRaw, vacancyRateRaw],
    );

    const headers = generateHeaders(
      rentForecast?.year ?? '',
      actualDateRange,
      differenceCount,
      enableAutoCalculate,
      budgetType,
      setOpenAutoCalculate,
      rentForecast?.createdAt,
      !rentForecast?.parameters?.intacctYTDActuals,
    );

    const handleAutoCalculateSubmit = () => {
      fieldCalculations
        .filter(
          ({ manualTriggerOnly, destinationField }) =>
            manualTriggerOnly === true && destinationField.startsWith('0-'),
        )
        .forEach((configuration) => {
          configuration.onChange({
            ...configuration,
            useFormReturn,
            triggerType: 'manual',
          });
        });

      setOpenAutoCalculate(false);

      toastr.success(
        intl.formatMessage(rentForecastMessages.success),
        intl.formatMessage(rentForecastMessages.autoCalculationSuccess),
      );
    };

    const renderAll = getQueryStringValue('fullRenderTable') === 'true';

    return (
      <>
        <AutoCalculateModal
          open={openAutoCalculate}
          body={intl.formatMessage(messages.autoCalculatePart1ModalBody)}
          warningText={intl.formatMessage(
            messages.autoCalculatePart1ModalWarning,
          )}
          closeModal={() => setOpenAutoCalculate(false)}
          onSubmit={handleAutoCalculateSubmit}
        />
        <TableBudget
          fieldNamePrefix={'0'}
          columns={headers}
          sections={sections}
          useFormReturn={useFormReturn}
          tableBodySize={{ height: 800, width: 1488 }}
          highlightFields={highlightFields}
          renderAll={renderAll}
        />
      </>
    );
  },
);

export default Part1;
